import { Edge, Node } from "@xyflow/react";
import { CallFlowActionType } from "@/model/interface/CallFlowActionType";
import { CallFlowType } from "@/model/interface/CallFlowType";
import { ActionNodeData } from "@/model/interface/CallFlow";
import { edgeLabelFor, isTerminalNodeType } from "@/util/callFlowNodes";

export type TemplateReference = "member" | "ringMembers" | "aiAgent";

export interface CallFlowTemplate {
  key: string;
  name: string;
  description: string;
  type: CallFlowType;
  references: TemplateReference[];
  build: (references: TemplateReferenceValues) => { nodes: Node[]; edges: Edge[] };
}

export interface TemplateReferenceValues {
  membershipId?: string;
  memberName?: string;
  ringMemberIds?: string[];
  aiAgentId?: string;
}

const START_NODE: Node = {
  id: "start",
  type: "start",
  position: { x: 0, y: 0 },
  data: {},
};

function actionNode(id: string, actionType: CallFlowActionType, label: string, index: number, data: Partial<ActionNodeData> = {}): Node {
  return {
    id,
    type: isTerminalNodeType(actionType) ? "end" : "action",
    position: { x: 0, y: (index + 1) * 200 },
    data: {
      actionType,
      label,
      ...data,
    } as ActionNodeData,
  };
}

function edge(source: string, target: string, sourceHandle?: string): Edge {
  return {
    id: `edge-${source}-${sourceHandle ?? "next"}-${target}`,
    source,
    target,
    sourceHandle,
    label: sourceHandle ? edgeLabelFor(sourceHandle) : undefined,
  };
}

function voicemailNode(id: string, index: number): Node {
  return actionNode(id, CallFlowActionType.VOICEMAIL, "Voicemail", index, {
    prompt: { mode: "SAY", text: "Sorry we missed you. Leave a message and we will call you back." },
    voicemailMaxLengthSeconds: 300,
    voicemailTranscribe: false,
    voicemailGreetingSource: "STEP",
  });
}

export const CALL_FLOW_TEMPLATES: CallFlowTemplate[] = [
  {
    key: "solo",
    name: "Solo",
    description: "Rings you, and takes a voicemail when you cannot pick up.",
    type: CallFlowType.BASIC,
    references: ["member"],
    build: ({ membershipId, memberName }) => ({
      nodes: [
        START_NODE,
        actionNode("ring-me", CallFlowActionType.CALL_USER, memberName ?? "Ring me", 0, {
          targetMembershipId: membershipId,
          targetUserName: memberName,
          timeoutSeconds: 30,
        }),
        voicemailNode("voicemail", 1),
      ],
      edges: [
        edge("start", "ring-me"),
        edge("ring-me", "voicemail", "noAnswer"),
      ],
    }),
  },
  {
    key: "team-ivr",
    name: "Team IVR",
    description: "Open hours give callers a menu, sales rings the team, everything else takes a message.",
    type: CallFlowType.BASIC,
    references: ["ringMembers"],
    build: ({ ringMemberIds }) => ({
      nodes: [
        START_NODE,
        actionNode("hours", CallFlowActionType.BUSINESS_HOURS, "Business hours", 0),
        actionNode("menu", CallFlowActionType.MENU, "Menu", 1, {
          prompt: { mode: "SAY", text: "Press 1 to speak to our team, or hold to leave a message." },
          menuKeys: ["1"],
          menuKeyLabels: { "1": "Speak to the team" },
          menuRepeatCount: 1,
        }),
        actionNode("team", CallFlowActionType.RING_GROUP, "Ring the team", 2, {
          ringMembers: ringMemberIds ?? [],
          ringStrategy: "SIMULTANEOUS",
          ringSeconds: 30,
        }),
        voicemailNode("voicemail", 3),
      ],
      edges: [
        edge("start", "hours"),
        edge("hours", "menu", "open"),
        edge("hours", "voicemail", "closed"),
        edge("menu", "team", "key:1"),
        edge("menu", "voicemail", "noInput"),
        edge("team", "voicemail", "noAnswer"),
      ],
    }),
  },
  {
    key: "after-hours-ai",
    name: "After-hours AI",
    description: "Your team answers during opening hours, an AI agent picks up the rest of the time.",
    type: CallFlowType.BASIC,
    references: ["ringMembers", "aiAgent"],
    build: ({ ringMemberIds, aiAgentId }) => ({
      nodes: [
        START_NODE,
        actionNode("hours", CallFlowActionType.BUSINESS_HOURS, "Business hours", 0),
        actionNode("team", CallFlowActionType.RING_GROUP, "Ring the team", 1, {
          ringMembers: ringMemberIds ?? [],
          ringStrategy: "SIMULTANEOUS",
          ringSeconds: 30,
        }),
        actionNode("agent", CallFlowActionType.AI_AGENT, "AI agent", 2, {
          aiAgentId,
        }),
        voicemailNode("voicemail", 3),
      ],
      edges: [
        edge("start", "hours"),
        edge("hours", "team", "open"),
        edge("hours", "agent", "closed"),
        edge("team", "voicemail", "noAnswer"),
      ],
    }),
  },
];

export function templateByKey(key: string): CallFlowTemplate | undefined {
  return CALL_FLOW_TEMPLATES.find((template) => template.key === key);
}
